import React from 'react';
import { Box, Typography } from '@mui/material';

type PrivacyPolicyProps = {
  children?: React.ReactNode;
};

type Subsection = {
  subtitle: string;
  text?: string;
  items?: string[];
  bullets?: string[];
};

const sections = [
  {
    title: '1. Definition',
    content: [
      {
        bold: 'Personal Data',
        text: ' means any information related to a person, which enables the identification of such person, whether directly or indirectly. For example, name, address, e-mail address, identification number.',
      },
      {
        bold: 'Sensitive Personal Data',
        text: ' is special category of personal data that needs appropriate protection and carefully collect, use, or disclose. Sensitive Personal Data pertains racial, ethnic origin, political opinions, cult, religious or philosophical beliefs, sexual behaviour, criminal records, health data, disability, trade union information, genetic data, and biometric data.',
      },
      {
        bold: 'Data Subject',
        text: ' means the owner of the personal data whose data is collected, used or disclosed.',
      },
    ],
  },
  {
    title: '2. The Purpose of Personal Data Processing',
    items: [
      'To verify and identify the Data Subject prior to accessing the Company\'s system, and for the purpose of conducting various operations or communications with the Company, including but not limited to monitoring, responding to inquiries, questions, or any requests made by the Data Subject.',
      'To perform the Company\'s obligations under a contract to which the Data Subject is a party, or to take steps at the request of the Data Subject prior to entering into such contract.',
      'For research and statistical analysis, including the development of new products and services, or the assessment and improvement of the Company\'s existing products and services.',
      'For marketing purposes relating to the Company\'s products and services, as well as those of the Company\'s affiliates and business partners, such as notifying the Data Subject of recent activities, special offers, and promotions, and/or allowing the Company to market products and services that may be of particular relevance or interest to the Data Subject.',
    ],
  },
  {
    title: '3. Personal Data for Personal Data Processing',
    subsections: [
      {
        subtitle: '3.1 Source of the Personal Data',
        items: [
          'Directly collect from the Data Subject; for instance, a collecting of the personal data from filling in the Company\'s form, registration processes, or entering into the Company\'s website via cookies.',
          'Indirectly collect from other sources which is not directly from the Data Subject; for instance, asking for the data from a third party. The Company shall notify the Data Subject and request a consent from the Data Subject, without delay, within 30 days from the date which the Company collects the personal data from such source.',
          'Collecting the personal data from a minor, an incompetent person and a quasi-incompetent person, the Company shall take into account of a personal data protection at the highest level as required by laws.',
        ],
      },
      {
        subtitle: '3.2 Types of the Personal Data to be Processed',
        bullets: [
          'Personal Data such as name, surname, date of birth',
          'Contact Data such as address, telephone number',
          'Financial Information such as credit card numbers, debit card numbers',
          'Account Information such as email address and password',
          'Technical Information such as Internet Protocol (IP) address, browser type, time zone settings, location data, operating system, platform, and other technologies used to access the Company\'s website.',
        ],
      },
    ],
  },
  {
    title: '4. Usage and Disclosure of the Personal Data',
    intro: 'The Company may disclose the Personal Data if necessary, for the purpose of complying with the informing Company\'s objectives to a third party and/or any other organizations or entities as follows:',
    subsections: [
      {
        subtitle: '4.1',
        text: 'Trading partners, business partners of the Company, subsidiary, affiliated and/or other service providers, including financial institution. Disclosing the personal data to such third party and/or any other organizations or entities, the Company shall manage those mentioned persons to keep the personal data in secret and not to use it for any other purposes rather than the scope set forth by the Company.',
      },
      {
        subtitle: '4.2',
        text: 'Government sector which has a legal authority according to the laws; for the purpose of complying with the laws, orders, petitions; in order to coordinate with any sectors in relation to the legal compliance.',
      },
    ],
  },
  {
    title: '5. Personal Data Collection Period',
    subsections: [
      {
        subtitle: '5.1',
        text: 'The Company shall collect the Personal Data of the Data Subject for a necessary period for the purpose of the Company\'s objective in accordance with a type of the personal data; except, there is a permission by laws providing a period extension for collection. In the event that the personal data collection period cannot be precisely specified, the Company shall collect the personal data for a foreseeable period in accordance with a collection standard. Once such collection period is expired, the Company shall delete or destroy such personal data.',
      },
      {
        subtitle: '5.2',
        text: 'In the case that the Company uses the Personal Data of the Data Subject with his consent, the Company shall process such personal data until the Data Subject notifies to cancel such consent and the Company has completely canceled such consent as requested. In this regard, the Company shall collect necessary part of the personal data for recording a cancellation of such consent by the Data Subject.',
      },
    ],
  },
  {
    title: '6. Transferring the Personal Data to a Foreign Country',
    subsections: [
      {
        subtitle: '6.1',
        text: 'The Company may deliver or transfer the personal data of the Data Subject to an affiliated company or any other persons in a foreign country in a necessary case to complete the Company\'s objectives which have been informed to the Data Subject.',
      },
      {
        subtitle: '6.2',
        text: 'In the event of delivering the personal data of the Data Subject to a foreign country, the Company shall comply with the Personal Data Protection Law and use a suitable measure to ensure that the Personal Data of the Data Subject is protected. The Company shall provide that a person who receives the data shall have a suitable protection measure to protect the Personal Data of the Data Subject and processes such personal data merely in a necessary manner.',
      },
    ],
  },
  {
    title: '7. The Rights of the Data Subject under the Laws',
    intro: 'The Data Subject has the rights as follows:',
    subsections: [
      { subtitle: '7.1 Right to Withdraw a Consent', text: 'The Data Subject has the right to withdraw a consent of Processing the personal data which has been given to the Company at all time. In this regard, the withdrawal shall not affect a part of the personal data which has been Processing with lawfully consent given by the Data Subject.' },
      { subtitle: '7.2 Right to Access and Request Receive a Copy of the Personal Data', text: 'The Data Subject can inform the Company to provide a copy of the personal data and has the right to inform the Company to disclose an acquisition of the personal data where such personal data has been given without the Data Subject\'s consent.' },
      { subtitle: '7.3 Right to Request Receive to Send or Transfer the Personal Data', text: 'The Data Subject can request the Company to send the data to other persons and request to receive the personal data which the Company has sent or transferred to other persons.' },
      { subtitle: '7.4 Right to Object Collection, Usage or Disclosure of the Personal Data', text: 'The Data Subject can object collection, usage or disclosure of the personal data upon there is no necessity to collect, use and disclose the personal data; or upon a collection, usage or disclosure for a purpose of direct marketing.' },
      { subtitle: '7.5 Right to Request for Deleting or Destroying the Personal Data', text: 'The Data Subject can request the Company to delete or destroy the personal data upon there is no further necessity of collection; or upon there is an unlawful Processing of the personal data.' },
      { subtitle: '7.6 Right to Request for a Usage Suspension of the Personal Data', text: 'The Data Subject can inform the Company to suspend the personal data usage during a personal data correctness inspection as requested by the Data Subject for a correctness amending of such data; or as requested to suspend instead of deleting the data.' },
      { subtitle: '7.7 Right to Request for an Alteration/Addition of the Personal Data', text: 'The Data Subject can request the Company to amend an incorrect, not-up-to-date, misleading personal data and/or to add the incomplete data.' },
      { subtitle: '7.8 Right to Complain', text: 'The Data Subject has the right to complain in the event that the Company violates or does not comply with the Personal Data Protection Act or with a declaration issued in accordance with the Personal Data Protection Act.' },
    ],
    footer: 'The Data Subject can exercise the rights which provided by law by contacting the Company in accordance with Section 9 of this Policy. The Company shall proceed with and consider the complaint in accordance with the rights of the Data Subject within the timeframe prescribed by laws. In this regard, the Company shall reserve the rights to refuse to proceed the Data Subject\'s right under the conditions of the laws or under any agreements with the Company.',
  },
  {
    title: '8. Security Measure',
    text: 'The Company has adopted the technical security standard and appropriate management in order to protect the personal data from an unauthorized accessing usage or disclosure; misuse; loss; destruction, by using the technology standard for security such as using a password and access restriction, in order to ensure that only an authorized person can access to the personal data of the Data Subject. The Company has provided an appropriate security measure to prevent loss; access; usage; alteration; amendment; disclosure of the personal data from an unauthorized person or a person who is not related to the personal data.',
  },
  {
    title: '9. The Information of the Data Controller (the Company)',
    lines: [
      'Iris 2005 Co., Ltd.',
      'Address: 723/1 Si Phraya Road, Bang Rak Sub-district, Bang Rak District, Bangkok 10500',
      'Contact: [•]',
    ],
  },
  {
    title: '10. Personal Data Protection Policies of Other Websites',
    text: "This Privacy Policy applies solely to the services provided by the Company and the use of the Company's website. In the event that the Data Subject click on a link that directs the Data Subject to another website (including links appearing on the Company's website), the Data Subject are advised to review and comply with the privacy policy of such website separately from that of the Company.",
  },
  {
    title: '11. Amendments',
    text: "The Company reserves the right to make all additions, alterations, improvements to the personal data protection measure to be appropriated in accordance with the Personal Data Protection Act. In the event of any amendments, the Company shall notify you of the updated measures via email.",
  },
];

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = () => {
  return (
    <Box sx={{ mx: 'auto', mt: 4, mr: { xs: 0, md: 50 } }}>
      <Typography variant="h3" align="center" sx={{ mb: 6, fontFamily: 'Nandia' }}>
        Setting
      </Typography>

      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
        Privacy Policy
      </Typography>

      {/* Intro paragraph */}
      <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8, mb: 4 }}>
        IRIS 2005 Co., Ltd. (the "Company") places great importance on your rights and privacy ("Data Subject") and fully
        recognizes the trust you place in the Company when providing your personal data for the purposes of conducting
        business and/or providing services to you. The Company will collect, use, and disclose ("Processing") your
        personal data with due care and in accordance with the principles of appropriateness as set forth in this Privacy
        Policy. Your personal data will be protected under the Personal Data Protection Act B.E. 2562 (2019) and all
        relevant subordinate legislation.
      </Typography>

      {sections.map((section, i) => (
        <Box key={i} sx={{ mb: 4 }}>
          {/* Section Title */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            {section.title}
          </Typography>

          {/* Intro text */}
          {section.intro && (
            <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8, mb: 1.5 }}>
              {section.intro}
            </Typography>
          )}

          {/* Plain text */}
          {section.text && (
            <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
              {section.text}
            </Typography>
          )}

          {/* Lines (contact info etc.) */}
          {section.lines && (
            <Box>
              {section.lines.map((line, j) => (
                <Typography key={j} variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                  {line}
                </Typography>
              ))}
            </Box>
          )}

          {/* Definition-style content (bold term + text) */}
          {section.content && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {section.content.map((def, j) => (
                <Typography key={j} variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                  <strong>{def.bold}</strong>
                  {def.text}
                </Typography>
              ))}
            </Box>
          )}

          {/* Numbered items */}
          {section.items && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pl: 2 }}>
              {section.items.map((item, j) => (
                <Typography key={j} variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                  • {item}
                </Typography>
              ))}
            </Box>
          )}

          {/* Subsections */}
          {section.subsections && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 2 }}>
              {section.subsections.map((sub: Subsection, j) => (
                <Box key={j}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#333', mb: 0.5 }}>
                    {sub.subtitle}
                  </Typography>
                  {sub.text && (
                    <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                      {sub.text}
                    </Typography>
                  )}
                  {sub.items && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 2 }}>
                      {sub.items.map((item, k) => (
                        <Typography key={k} variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                          • {item}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {sub.bullets && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 2 }}>
                      {sub.bullets.map((b, k) => (
                        <Typography key={k} variant="body2" sx={{ color: '#333', lineHeight: 1.8 }}>
                          - {b}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Footer note (section 7) */}
          {section.footer && (
            <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.8, mt: 1.5 }}>
              {section.footer}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default PrivacyPolicy;
